import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import {
    ArrowUpNarrowWide,
    ArrowUpWideNarrow,
    CalendarIcon,
    Download,
    FileText,
    FolderInput,
    FolderOutput,
    LogIn,
    Printer,
    SquareArrowOutUpRight
} from "lucide-react";
import Button from "../../../../../components/shared/Button";
import DateFilters from "../../../../../components/shared/filters/DateFilters";
import QuickFilters from "../../../../../components/shared/filters/QuickFilters";
import ViewOptions from "./ViewOptions";
import Filters from "./Filters";
import BulkActions from "./BulkActions";
import Widgets from "./Widgets";
import Select from "../../../../../components/shared/inputs/Select";
import WeekdaySelect from "../../../../../components/shared/inputs/WeekdaySelect";
import ScheduleTemplates from "./ScheduleTemplates";
import { useScheduleContext } from "../../hooks/context/useScheduleContext";
import { useIsTemplate } from "../../hooks/useIsTemplate";
import { useExportScheduleQuery } from "../../api/useExportScheduleQuery";
import { usePermissions } from "../../hooks/usePermissions";
import { getOrderedWeekdays } from "../../helpers/getOrderedWeekdays";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import { useAccess } from "../../../../../utils/hooks/access";
import useApi from "../../../../../utils/api";
import { usePreferences } from "../../../../Settings/Configuration/hooks/usePreferences";
import { formatTimeSheetParams } from "../../../../TimeSheet/api/helpers";
import { getErrorMessage } from "../../../../../utils/helpers/errors";
import { firstToUpper } from "../../../../../utils/helpers/string";
import { withLoadingToast } from "../../helpers/withLoadingToast";
import { SCHEDULE_PREFERENCE_KEYS } from "../../constants/scheduleTypes";
import { cn } from "../../../../../lib/utils";

function Topbar({
    openActionsModal,
    openCopyModal,
    openDeleteModal,
    openRequestDrawer,
    openLoadTemplatesModal,
    openApproveModal,
    openDenyModal,
    openImportModal
}: any) {
    const { t } = useTranslation();
    const [, setSearchParams] = useSearchParams();
    const [hideWidget, setHideWidget] = useState<boolean>(true);
    const { authPut } = useApi();
    const [filters] = useFilters();
    const { dispatch, type, count, showCurrentTime, showUnassigendShifts } = useScheduleContext();
    const isTemplate = useIsTemplate();
    const canApproveEvent = useAccess("schedule.canApproveMySchedule");
    const canCreate = useAccess("schedule.canCreateMySchedule");
    const { canView, canViewUnassignedShifts } = usePermissions();
    const isTeamScheduler = type === "team";
    const isProfile = ["profile", "my-profile"].includes(type);
    const orderedWeekdays = getOrderedWeekdays();
    const exportSchedule = useExportScheduleQuery();
    const hideSort = usePreferences(`${SCHEDULE_PREFERENCE_KEYS[type]}.hideSort`);
    const hidePrint = usePreferences(`${SCHEDULE_PREFERENCE_KEYS[type]}.hidePrint`);

    const onSortClick = useCallback(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (filters?.sort) {
                next.delete("sort");
            } else {
                next.set("sort", "eventStartDate");
            }
            return next;
        });
    }, [setSearchParams, filters?.sort]);

    const bulkSubmitMutation = useMutation({
        ...withLoadingToast(t(isTeamScheduler ? "publishing" : "submitting")),
        mutationFn: () => {
            const endpoint = isTeamScheduler
                ? `/schedules/v2/publish/bulk`
                : `/my/schedules/v2/submit/bulk`;
            const data = {
                filters: {
                    ...formatTimeSheetParams({
                        filters: filters?.filters || [],
                        from: filters?.mode === "month" ? moment(filters?.from).startOf("week").format("YYYY-MM-DD") : filters?.from,
                        to: filters?.mode === "month" ? moment(filters?.to).endOf("week").format("YYYY-MM-DD") : filters?.to
                    }),
                    pageIndex: Number(filters?.page || 1) - 1,
                    pageSize: Number(filters?.pageSize || 50),
                    sort: filters?.sort,
                },
                from: filters?.mode === "month" ? moment(filters?.from).startOf("week").format("YYYY-MM-DD") : filters?.from,
                to: filters?.mode === "month" ? moment(filters?.to).endOf("week").format("YYYY-MM-DD") : filters?.to
            };
            return authPut(endpoint, { data });
        },
        onSuccess: (response: any) => {
            const success = response?.filter((item: any) => item?.status)?.length || 0;
            dispatch({ type: "refetch-events" });
            dispatch({ type: "set-count", payload: { count: Number(count || 0) - success } })
        },
        onError: (err) => getErrorMessage(err, t),
    });

    const onExport = useCallback(() => {
        const isMonth = filters?.mode === "month";
        const isPaged = type === "team" || type === "template";
        const baseParams: any = {
            filters: filters?.filters ?? [],
            sort: filters?.sort,
        };

        if (!isTemplate) {
            baseParams.startDate = isMonth
                ? moment(filters?.from).startOf("week").format("YYYY-MM-DD")
                : moment(filters?.from).format("YYYY-MM-DD");

            baseParams.endDate = isMonth
                ? moment(filters?.to).endOf("week").format("YYYY-MM-DD")
                : moment(filters?.to).format("YYYY-MM-DD");
        } else {
            baseParams.mode = filters?.mode;
            baseParams.day = filters?.day;
        }

        if (isPaged && !isMonth) {
            baseParams.page = Number(filters?.page || 1) - 1;
            baseParams.perPage = filters?.pageSize || 50;
        }
        exportSchedule.mutate(baseParams);
    }, [exportSchedule, filters, type, isTemplate]);

    const onShowCurrentTimeChange = useCallback(() => {
        dispatch({ type: "show-current-time" });
    }, [dispatch]);

    const onUnassignedShiftsVisibility = useCallback(() => {
        dispatch({ type: "unassigned-shifts-visible" });
    }, [dispatch]);

    const onModeChange = useCallback((value: string) => {
        const day = value === "day" ? orderedWeekdays[moment().weekday()] : undefined;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("mode", value);
            if (day) {
                next.set("day", day);
            } else {
                next.delete("day");
            }
            return next;
        });
    }, [setSearchParams, orderedWeekdays]);

    const onDayChange = useCallback((value: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("day", value);
            return next;
        });
    }, [setSearchParams]);

    useEffect(() => {
        if (!filters?.mode && !["team", "home"].includes(type)) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("mode", "week");
                return next;
            }, { replace: true });
        }
    }, [filters?.mode, type, setSearchParams]);

    return (
        <>
            <div className={cn("tw-flex tw-flex-col", ["team", "home"].includes(type) ? "tw-mb-3" : "tw-mb-1", !hideWidget && "tw-mb-4")}>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mb-7">
                    {["team", "home"].includes(type) && (
                        <div>
                            <DateFilters />
                        </div>
                    )}
                    {type === "template" && (
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <Select
                                className="min-w-30"
                                value={filters?.mode || "week"}
                                onChange={onModeChange}
                                showSearch={false}
                            >
                                <Select.Item value="day">{firstToUpper(t("day"))}</Select.Item>
                                <Select.Item value="week">{t("week")}</Select.Item>
                            </Select>

                            {filters?.mode === "day" && (
                                <WeekdaySelect
                                    className="min-w-35"
                                    value={filters?.day}
                                    onChange={onDayChange}
                                />
                            )}
                        </div>
                    )}
                    {["team", "home"].includes(type) && (
                        <div
                            className={cn(
                                "tw-border tw-rounded-sm tw-p-2 tw-cursor-pointer",
                                showCurrentTime && "tw-bg-primary/10"
                            )}
                            onClick={onShowCurrentTimeChange}
                        >
                            <CalendarIcon size={16} className="tw-text-primary" />
                        </div>
                    )}
                    {!isProfile && <QuickFilters />}
                </div>

                <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        {/* <Button
                            className="tw-font-normal tw-px-4 tw-text-neutral-900 tw-h-9"
                            variant="outline"
                            onClick={() => setHideWidget((prev: boolean) => !prev)}
                            hidden={!["team", "home"].includes(type)}
                        >
                            {!hideWidget ? <LogIn size={16} className="tw-rotate-180" /> : <LogIn size={16} />}
                            {hideWidget ? t("view-widget") : t("hide-widget")}
                        </Button> */}

                        {!isProfile && <Filters />}
                        {!isProfile && <ViewOptions />}
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <div className="tw-flex tw-items-center">
                            <Button
                                variant="ghost"
                                onClick={onSortClick}
                                hidden={!(filters?.mode === "day" && ["team", "home"].includes(type)) || hideSort}
                            >
                                {filters?.sort
                                    ? <ArrowUpWideNarrow />
                                    : <ArrowUpNarrowWide />
                                }
                            </Button>

                            <Button
                                variant="ghost"
                                onClick={() => window.print()}
                                hidden={type === "my-profile" || hidePrint}
                            >
                                <Printer />
                            </Button>

                            {/* <Button
                                variant="ghost"
                                onClick={onExport}
                                hidden={!(canView && ["team", "home"].includes(type))}
                                disabled={exportSchedule.isPending}
                                loading={exportSchedule.isPending}
                            >
                                <Download />
                            </Button> */}
                        </div>

                        <Button
                            className="tw-font-normal tw-px-4 tw-text-neutral-900 tw-h-9"
                            variant="outline"
                            onClick={openRequestDrawer}
                            hidden={type !== "home"}
                        >
                            {t("requests")}
                        </Button>

                        <Button
                            className="tw-font-normal tw-px-4 tw-text-neutral-900 tw-h-9"
                            variant="outline"
                            onClick={() => openLoadTemplatesModal({ type: "all" })}
                            // onClick={() => loadTemplatesQuery.mutate()}
                            hidden={!(canApproveEvent && type === "team")}
                        >
                            <FileText />
                            {t("load-templates")}
                        </Button>

                        <Button
                            className="tw-font-normal tw-px-4 tw-h-9"
                            disabled={count === 0 || bulkSubmitMutation.isPending}
                            onClick={() => bulkSubmitMutation.mutate()}
                            loading={bulkSubmitMutation.isPending}
                            hidden={!((canApproveEvent && type === "team") || (canCreate && type === "home"))}
                        >
                            <SquareArrowOutUpRight />
                            {t(isTeamScheduler ? "publish" : "submit")}{` (${count})`}
                        </Button>

                        {type !== "my-profile" && (
                            <BulkActions
                                openActionsModal={openActionsModal}
                                openCopyModal={openCopyModal}
                                openDeleteModal={openDeleteModal}
                                openImportModal={openImportModal}
                            />
                        )}

                        <Button
                            className="tw-font-normal tw-px-4 tw-text-neutral-900 tw-h-9"
                            variant="outline"
                            onClick={onUnassignedShiftsVisibility}
                            tooltip={!showUnassigendShifts
                                ? t("show-unassigned-shifts")
                                : t("hide-unassigned-shifts")}
                            hidden={!(type === "team" && filters?.mode !== "month" && canViewUnassignedShifts)}
                        >
                            {!showUnassigendShifts ? <FolderOutput /> : <FolderInput />}
                        </Button>
                    </div>
                </div>
            </div>

            {["profile", "my-profile", "template"].includes(type) && (
                <div className="tw-flex tw-items-center tw-justify-between">
                    {isProfile ? (
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <Select
                                className="min-w-30"
                                value={filters?.mode || "week"}
                                onChange={onModeChange}
                                showSearch={false}
                            >
                                <Select.Item value="day">{firstToUpper(t("day"))}</Select.Item>
                                <Select.Item value="week">{t("week")}</Select.Item>
                            </Select>

                            {filters?.mode === "day" && (
                                <WeekdaySelect
                                    className="min-w-35"
                                    value={filters?.day}
                                    onChange={onDayChange}
                                />
                            )}

                            <ViewOptions />
                        </div>
                    ) : <div />}
                    <ScheduleTemplates />
                </div>
            )}

            {!hideWidget && (
                <Widgets
                    approve={openApproveModal}
                    deny={openDenyModal}
                />
            )}
        </>
    );
}

export default Topbar;
